import React, { useEffect, useRef, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Trash2, LayoutTemplate, Plus, Pencil, X } from "lucide-react";
import { toast, Toaster } from "sonner";

const EMPTY_MED = { brand_name: "", strength: "", frequency: "", duration: "" };

export default function Templates() {
  const [rows, setRows] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/templates");
    setRows(r.data.templates || []);
  };
  useEffect(() => { load(); }, []);

  const del = async (tid) => {
    if (!confirm("Delete this template?")) return;
    await api.delete(`/templates/${tid}`);
    toast.success("Template deleted");
    load();
  };

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (t) => { setEditing(t); setEditorOpen(true); };

  const save = async (draft) => {
    if (!draft.name.trim()) { toast.error("Template name is required"); return; }
    const meds = (draft.medicines || []).filter((m) => m.brand_name.trim());
    await api.post("/templates", { ...draft, medicines: meds });
    toast.success(editing ? "Template updated" : "Template created");
    setEditorOpen(false);
    load();
  };

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">LIBRARY</div>
          <h1 className="font-heading text-3xl font-semibold mt-1 text-slate-900">Prescription templates</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-lg">
            Reusable templates load a set of medicines, diagnosis and advice into any visit in one click.
            Create them here or save from any patient's prescription screen.
          </p>
        </div>
        <Button onClick={openNew} className="bg-pink-600 hover:bg-pink-700 text-white gap-2" data-testid="new-template-btn">
          <Plus size={14} /> New template
        </Button>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {rows.map((t) => (
          <div key={t.template_id} className="bg-white border border-slate-200 rounded-md p-5" data-testid={`tpl-${t.template_id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading text-lg font-semibold flex items-center gap-2">
                  <LayoutTemplate size={16} className="text-pink-600" />
                  {t.name}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {t.diagnosis || "—"} {t.icd10 && <span className="tabular">· {t.icd10}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(t)} className="p-1.5 rounded text-slate-500 hover:bg-slate-100" data-testid={`edit-tpl-${t.template_id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => del(t.template_id)} className="p-1.5 rounded text-red-600 hover:bg-red-50" data-testid={`del-tpl-${t.template_id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div className="mt-3 space-y-1">
              {(t.medicines || []).map((m, i) => (
                <div key={i} className="text-xs flex justify-between border-b border-slate-100 py-1">
                  <span className="font-medium">{m.brand_name} <span className="text-slate-500 font-normal">{m.strength}</span></span>
                  <span className="text-slate-500 tabular">{m.frequency} · {m.duration}</span>
                </div>
              ))}
              {(t.medicines || []).length === 0 && <div className="text-xs text-slate-400 italic">No medicines</div>}
            </div>
            {t.advice && <div className="text-[11px] text-slate-600 mt-3 leading-relaxed"><strong>Advice:</strong> {t.advice}</div>}
            <div className="text-[10px] text-slate-400 mt-3">By {t.created_by} · Updated {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : "—"}</div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-2 border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500">
            No templates yet. Click "New template" or save one from any patient's prescription screen.
          </div>
        )}
      </div>

      <TemplateEditorDialog
        key={editing ? editing.template_id : "new"}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        template={editing}
        onSave={save}
      />
    </Layout>
  );
}

function TemplateEditorDialog({ open, onClose, template, onSave }) {
  const [d, setD] = useState(() => template
    ? { ...template, medicines: (template.medicines || []).map((m) => ({ ...EMPTY_MED, ...m })) }
    : { name: "", diagnosis: "", icd10: "", advice: "", medicines: [{ ...EMPTY_MED }] });

  const setMed = (i, patch) => setD((p) => ({
    ...p,
    medicines: p.medicines.map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
  }));
  const addMed = () => setD((p) => ({ ...p, medicines: [...p.medicines, { ...EMPTY_MED }] }));
  const removeMed = (i) => setD((p) => ({ ...p, medicines: p.medicines.filter((_, idx) => idx !== i) }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <LayoutTemplate size={16} className="text-pink-600" />
            {template ? "Edit template" : "New template"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="overline">TEMPLATE NAME *</Label>
              <Input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })}
                placeholder="e.g. Type 2 Diabetes Mellitus" className="mt-1" data-testid="tpl-name-input" />
            </div>
            <div>
              <Label className="overline">DIAGNOSIS</Label>
              <Input value={d.diagnosis || ""} onChange={(e) => setD({ ...d, diagnosis: e.target.value })}
                placeholder="Primary diagnosis" className="mt-1" data-testid="tpl-diagnosis-input" />
            </div>
            <div>
              <Label className="overline">ICD-10</Label>
              <Input value={d.icd10 || ""} onChange={(e) => setD({ ...d, icd10: e.target.value })}
                placeholder="e.g. E11.9" className="mt-1 tabular" data-testid="tpl-icd10-input" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="overline">MEDICINES</Label>
              <button onClick={addMed} className="text-xs text-pink-700 hover:text-pink-800 flex items-center gap-1" data-testid="tpl-add-med-btn">
                <Plus size={12} /> Add medicine
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {d.medicines.map((m, i) => (
                <MedRow key={i} med={m} idx={i} onChange={(patch) => setMed(i, patch)} onRemove={() => removeMed(i)} />
              ))}
              {d.medicines.length === 0 && (
                <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded p-3 text-center">
                  No medicines — a template can carry just diagnosis and advice.
                </div>
              )}
            </div>
          </div>

          <div>
            <Label className="overline">ADVICE</Label>
            <textarea
              value={d.advice || ""}
              onChange={(e) => setD({ ...d, advice: e.target.value })}
              rows={2}
              placeholder="e.g. Diabetic diet. Walk 30 min daily."
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-y"
              data-testid="tpl-advice-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(d)} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="tpl-save-btn">
            {template ? "Save changes" : "Create template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MedRow({ med, idx, onChange, onRemove }) {
  const [sugg, setSugg] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const timer = useRef(null);

  const onBrand = (v) => {
    onChange({ brand_name: v });
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setSugg([]); setShowSugg(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/medicines/search?q=${encodeURIComponent(v.trim())}&page_size=6`);
        setSugg(r.data.results || []);
        setShowSugg(true);
      } catch { /* ignore */ }
    }, 250);
  };

  const pick = (s) => {
    onChange({
      brand_name: s.brand_name || "",
      strength: s.strength || "",
      frequency: med.frequency || s.frequency || "",
    });
    setShowSugg(false);
  };

  return (
    <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 items-start">
      <div className="relative">
        <Input
          value={med.brand_name}
          onChange={(e) => onBrand(e.target.value)}
          onBlur={() => setTimeout(() => setShowSugg(false), 150)}
          placeholder="Brand name"
          className="h-9 text-sm"
          data-testid={`tpl-med-brand-${idx}`}
        />
        {showSugg && sugg.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-44 overflow-y-auto">
            {sugg.map((s) => (
              <button
                key={s.medicine_id}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-pink-50"
                data-testid={`tpl-med-sugg-${s.medicine_id}`}
              >
                <span className="font-medium">{s.brand_name}</span>
                <span className="text-slate-500"> {s.strength} · {s.generic_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <Input value={med.strength} onChange={(e) => onChange({ strength: e.target.value })}
        placeholder="Strength" className="h-9 text-sm" data-testid={`tpl-med-strength-${idx}`} />
      <Input value={med.frequency} onChange={(e) => onChange({ frequency: e.target.value })}
        placeholder="OD / BD / TDS" className="h-9 text-sm" data-testid={`tpl-med-freq-${idx}`} />
      <Input value={med.duration} onChange={(e) => onChange({ duration: e.target.value })}
        placeholder="Duration" className="h-9 text-sm" data-testid={`tpl-med-duration-${idx}`} />
      <button onClick={onRemove} className="p-2 mt-0.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`tpl-med-remove-${idx}`}>
        <X size={14} />
      </button>
    </div>
  );
}
